import React, { useEffect, useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ConsequenceAlert } from './ConsequenceAlert';
import { humanizeError } from '../lib/errors';

interface ConfirmDialogProps {
  open: boolean;
  /** '{Verb} "{name}"?' in sentence case (DESIGN_SYSTEM §5 copy pattern). */
  title: string;
  /**
   * Consequence first: what stops or is lost (dependents, with a count when
   * known), then what is not affected, then the reversible alternative, then
   * "This can't be undone." Never restates the title, never "Are you sure".
   */
  description: React.ReactNode;
  /**
   * Required, verb + noun, reusing the verb of the control that opened the
   * dialog ("Delete lens", "Remove member"). There is no default: a bare
   * "Confirm" / "Delete" / "OK" fails lint:design.
   */
  confirmLabel: string;
  /** Defaults to 'Cancel'. A discard confirmation passes 'Keep editing'. */
  cancelLabel?: string;
  /**
   * 'destructive' (default): red confirm, for a rung-2 or rung-3 destruction.
   * 'default': a consequential decision that destroys nothing (granting
   * access, making a record public, discarding a draft).
   */
  variant?: 'destructive' | 'default';
  /**
   * Rung 3: irreversible AND stops live traffic or crosses a tenant boundary.
   * Typing this exact string (the record's name) enables the confirm button.
   * Passing it is what marks rung 3 — there is no separate severity prop.
   */
  typeToConfirm?: string;
  /**
   * A consequence that reaches beyond one organization, rendered as the amber
   * ConsequenceAlert between the description and the footer. It never changes
   * the confirm button's variant.
   */
  consequence?: React.ReactNode;
  /**
   * Lead-in for the inline failure line, e.g. "Couldn't delete the lens".
   * Passed to humanizeError, so a failure reads "Couldn't delete the lens.
   * You do not have permission…".
   */
  errorContext?: string;
  /**
   * Async handler — throw to show an inline error (the dialog stays open),
   * resolve to close. Never catch and swallow a failure in here: a resolved
   * promise is read as success and closes the dialog.
   */
  onConfirm: () => Promise<void>;
  onClose: () => void;
  /**
   * Radix's focus return when the dialog closes. After a SUCCESSFUL confirm
   * the default is always prevented: the control that opened the dialog was
   * usually on the record just destroyed, and DataTable moves focus to the row
   * that took its place (DESIGN_SYSTEM §8.6). Cancel and Escape keep the
   * default, which lands back on the control that opened the confirmation.
   */
  onCloseAutoFocus?: (event: Event) => void;
}

/**
 * The portal's only confirmation pattern. window.confirm/alert are banned.
 * Contract: dismissal is locked while pending; a failure keeps the dialog open
 * with friendly copy; Escape and Cancel close it; an outside click does
 * nothing (it is a Radix AlertDialog, which ignores outside clicks on purpose).
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'destructive',
  typeToConfirm,
  consequence,
  errorContext,
  onConfirm,
  onClose,
  onCloseAutoFocus,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typedValue, setTypedValue] = useState('');
  /** True once onConfirm resolved: the close that follows skips Radix's focus return. */
  const confirmedRef = useRef(false);

  useEffect(() => {
    if (open) {
      setTypedValue('');
      setError(null);
      confirmedRef.current = false;
    }
  }, [open]);

  const typeGateSatisfied = !typeToConfirm || typedValue === typeToConfirm;

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onConfirm();
      confirmedRef.current = true;
      onClose();
    } catch (err: unknown) {
      console.error(`ConfirmDialog action failed (${title}):`, err);
      setError(humanizeError(err, errorContext));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isLoading) {
      setError(null);
      onClose();
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        aria-busy={isLoading || undefined}
        onCloseAutoFocus={(event) => {
          if (confirmedRef.current) event.preventDefault();
          onCloseAutoFocus?.(event);
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {consequence ? <ConsequenceAlert>{consequence}</ConsequenceAlert> : null}
        {typeToConfirm ? (
          <div className="space-y-2">
            <Label htmlFor="confirm-dialog-type-gate">
              Type <span className="font-mono font-medium">{typeToConfirm}</span> to confirm
            </Label>
            <Input
              id="confirm-dialog-type-gate"
              value={typedValue}
              onChange={(event) => setTypedValue(event.target.value)}
              placeholder={typeToConfirm}
              autoComplete="off"
              disabled={isLoading}
            />
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive-emphasis">
            {error}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{cancelLabel}</AlertDialogCancel>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            loading={isLoading}
            disabled={!typeGateSatisfied}
          >
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
